import {
  BadgeCheck,
  Boxes,
  Check,
  ChevronRight,
  CircleDollarSign,
  Clock,
  Handshake,
  HelpCircle,
  Server,
} from "lucide-react";
import { AndroidLogo } from "./AndroidLogo";
import { RevealSection } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

export function AospVsCertifiedAtvPage() {
  const { messages, site, href } = useSite();
  const u = (path: string) => localizeUrl(path, site.locale, href);
  const salesCta = u("/contacts?inquiry=sales#inquiry-form");

  const heroStats = [
    { icon: Clock, label: messages.acStat1 },
    { icon: CircleDollarSign, label: messages.acStat2 },
    { icon: Handshake, label: messages.acStat3 },
  ];

  const tableRows = [
    { dim: messages.acRow1Dim, a: messages.acRow1Aosp, c: messages.acRow1Cert },
    { dim: messages.acRow2Dim, a: messages.acRow2Aosp, c: messages.acRow2Cert },
    { dim: messages.acRow3Dim, a: messages.acRow3Aosp, c: messages.acRow3Cert },
    { dim: messages.acRow4Dim, a: messages.acRow4Aosp, c: messages.acRow4Cert },
    { dim: messages.acRow5Dim, a: messages.acRow5Aosp, c: messages.acRow5Cert },
    { dim: messages.acRow6Dim, a: messages.acRow6Aosp, c: messages.acRow6Cert },
    { dim: messages.acRow7Dim, a: messages.acRow7Aosp, c: messages.acRow7Cert },
    { dim: messages.acRow8Dim, a: messages.acRow8Aosp, c: messages.acRow8Cert },
    { dim: messages.acRow9Dim, a: messages.acRow9Aosp, c: messages.acRow9Cert },
    { dim: messages.acRow10Dim, a: messages.acRow10Aosp, c: messages.acRow10Cert },
    { dim: messages.acRow12Dim, a: messages.acRow12Aosp, c: messages.acRow12Cert },
    { dim: messages.acRow13Dim, a: messages.acRow13Aosp, c: messages.acRow13Cert },
    { dim: messages.acRow11Dim, a: messages.acRow11Aosp, c: messages.acRow11Cert },
  ];

  // The inext operations stack is identical on both — reuse the shared,
  // clickable solution labels from the Android-vs-Linux guide.
  const inextStack: { label: string; href?: string }[] = [
    { label: messages.avlStackAlcatraz, href: "/solutions/alcatraz-dms" },
    { label: messages.avlStackOta, href: "/solutions/ota-updates" },
    { label: messages.avlStackTr069 },
    { label: messages.avlStackCustomization, href: "/solutions/customization" },
  ];

  const aospSteps = [
    messages.acTimelineAospStep1,
    messages.acTimelineAospStep2,
    messages.acTimelineAospStep3,
  ];
  const certSteps = [
    messages.acTimelineCertStep1,
    messages.acTimelineCertStep2,
    messages.acTimelineCertStep3,
    messages.acTimelineCertStep4,
    messages.acTimelineCertStep5,
  ];

  const includes = [
    messages.acIncludes1,
    messages.acIncludes2,
    messages.acIncludes3,
    messages.acIncludes4,
    messages.acIncludes5,
  ];

  const faqs = [
    { q: messages.acFaqQ1, a: messages.acFaqA1 },
    { q: messages.acFaqQ7, a: messages.acFaqA7 },
    { q: messages.acFaqQ8, a: messages.acFaqA8 },
    { q: messages.acFaqQ9, a: messages.acFaqA9 },
    { q: messages.acFaqQ2, a: messages.acFaqA2 },
    { q: messages.acFaqQ3, a: messages.acFaqA3 },
    { q: messages.acFaqQ4, a: messages.acFaqA4 },
    { q: messages.acFaqQ5, a: messages.acFaqA5 },
    { q: messages.acFaqQ6, a: messages.acFaqA6 },
  ];

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={u("/")} className="hover:text-foreground">
            {messages.guidesBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={u("/guides")} className="hover:text-foreground">
            {messages.navGuides}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.acBreadcrumbPage}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div aria-hidden className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-40" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 top-0 -z-10 h-[440px] w-[600px] rounded-full opacity-40 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-24">
          <div className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            {messages.acHeroEyebrow}
          </div>
          <h1
            className="mt-4 animate-fade-up text-balance text-4xl font-semibold tracking-tight sm:text-5xl"
            style={{ animationDelay: "80ms" }}
          >
            {messages.acHeroTitle}{" "}
            <span className="text-gradient">{messages.acHeroTitleAccent}</span>
          </h1>
          <p
            className="mx-auto mt-6 max-w-2xl animate-fade-up text-pretty text-lg text-muted-foreground"
            style={{ animationDelay: "160ms" }}
          >
            {messages.acHeroSub}
          </p>
          <p
            className="mt-5 animate-fade-up text-pretty text-sm font-medium text-foreground/80"
            style={{ animationDelay: "220ms" }}
          >
            {messages.acHeroNote}
          </p>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-3"
            style={{ animationDelay: "280ms" }}
          >
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.acHeroCtaPrimary}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <a href="#comparison">{messages.acHeroCtaSecondary}</a>
            </Button>
          </div>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-2"
            style={{ animationDelay: "340ms" }}
          >
            {heroStats.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
              >
                <Icon className="h-3.5 w-3.5 text-primary" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Decision pivot — the one question */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.acPivotEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.acPivotTitle}
            </h2>
          </div>
          <div className="mt-8 rounded-2xl border border-primary/25 bg-card/70 p-6 shadow-glow sm:p-8">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HelpCircle className="h-5 w-5" />
              </span>
              <p className="text-lg font-medium leading-snug text-foreground">
                {messages.acPivotQuestion}
              </p>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-border/60 bg-background/60 p-5">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {messages.acPivotNoLabel}
                  </span>
                  <span className="shrink-0 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                    {messages.acPivotNoPick}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {messages.acPivotNoText}
                </p>
              </div>
              <div className="rounded-xl border border-border/60 bg-background/60 p-5">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {messages.acPivotYesLabel}
                  </span>
                  <span className="shrink-0 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    {messages.acPivotYesPick}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {messages.acPivotYesText}
                </p>
              </div>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">{messages.acPivotFootnote}</p>
          </div>
        </div>
      </RevealSection>

      {/* What each is — split panel */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.acDefineEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.acDefineTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{messages.acDefineBody}</p>
          </div>
          <div className="mt-8 grid divide-y divide-border/60 overflow-hidden rounded-2xl border border-border/60 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
            <div className="bg-card/70 p-6 sm:p-7">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <AndroidLogo className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-foreground">
                {messages.acDefineAospLabel}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {messages.acDefineAospBody}
              </p>
            </div>
            <div className="bg-card/40 p-6 sm:p-7">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <BadgeCheck className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-foreground">
                {messages.acDefineCertLabel}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {messages.acDefineCertBody}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* Two paths to launch — timeline */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.acTimelineEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.acTimelineTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{messages.acTimelineIntro}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="rounded-2xl border border-primary/30 bg-card/70 p-6 shadow-glow sm:p-7">
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                  <AndroidLogo className="h-5 w-5 text-primary" />
                  {messages.acTimelineAospLabel}
                </span>
                <span className="shrink-0 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                  {messages.acTimelineAospResult}
                </span>
              </div>
              <ol className="mt-5 space-y-3">
                {aospSteps.map((step, i) => (
                  <li key={step} className="flex items-start gap-3 text-sm">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                      {i + 1}
                    </span>
                    <span className="text-foreground/90">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card/50 p-6 sm:p-7">
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                  <BadgeCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  {messages.acTimelineCertLabel}
                </span>
                <span className="shrink-0 rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
                  {messages.acTimelineCertResult}
                </span>
              </div>
              <ol className="mt-5 space-y-3">
                {certSteps.map((step, i) => (
                  <li key={step} className="flex items-start gap-3 text-sm">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                      {i + 1}
                    </span>
                    <span className="text-foreground/90">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <p className="mt-6 rounded-xl border-l-2 border-primary/50 bg-muted/30 py-3 pl-4 pr-3 text-sm leading-relaxed text-foreground/90">
            {messages.acTimelineNote}
          </p>
        </div>
      </RevealSection>

      {/* Comparison table */}
      <RevealSection id="comparison" className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.acTableEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.acTableTitle}
            </h2>
          </div>
          <div className="mt-10 overflow-x-auto rounded-2xl border border-border/60 bg-card/60">
            <table className="w-full min-w-[680px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-muted-foreground">
                    {messages.acTableColDimension || " "}
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                      <AndroidLogo className="h-4 w-4 text-primary" />
                      {messages.acTableColAosp}
                    </span>
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                      <BadgeCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      {messages.acTableColCert}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => (
                  <tr key={r.dim} className="border-b border-border/60 align-top last:border-0">
                    <th scope="row" className="px-4 py-3 text-left font-medium text-foreground/90">
                      {r.dim}
                    </th>
                    <td className="px-4 py-3 text-muted-foreground">{r.a}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.c}</td>
                  </tr>
                ))}
                <tr className="bg-primary/[0.06]">
                  <th scope="row" className="px-4 py-3 text-left font-semibold text-foreground">
                    {messages.avlRowInextDim}
                  </th>
                  <td colSpan={2} className="px-4 py-3 font-medium text-foreground/90">
                    {[
                      <span key="stack-label">{messages.avlStackLabel} </span>,
                      ...inextStack.flatMap((item, idx) => {
                        const node = item.href ? (
                          <a
                            key={item.label}
                            href={u(item.href)}
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            {item.label}
                          </a>
                        ) : (
                          <span key={item.label}>{item.label}</span>
                        );
                        return idx === 0
                          ? [node]
                          : [
                              <span
                                key={`sep-${item.label}`}
                                aria-hidden
                                className="mx-1.5 text-muted-foreground/50"
                              >
                                ·
                              </span>,
                              node,
                            ];
                      }),
                    ]}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">{messages.acTableNote}</p>
        </div>
      </RevealSection>

      {/* What our AOSP includes — checklist */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.acIncludesEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.acIncludesTitle}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {messages.acIncludesBody}
          </p>
          <ul className="mt-6 space-y-3">
            {includes.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/60 px-4 py-3"
              >
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span className="text-sm text-foreground/90">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </RevealSection>

      {/* Both platforms, one partner */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-3xl">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.acSameEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.acSameTitle}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              {messages.acSameBody}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <article className="rounded-2xl border border-primary/30 bg-card/80 p-6 shadow-glow sm:p-8">
              <div className="flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Boxes className="h-5 w-5" />
                </span>
                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary">
                  1–2 weeks
                </span>
              </div>
              <h3 className="mt-5 text-xl font-semibold tracking-tight text-foreground">
                {messages.acInhouseTitle}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {messages.acInhouseBody}
              </p>
            </article>
            <article className="rounded-2xl border border-emerald-500/30 bg-card/70 p-6 sm:p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <Handshake className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-xl font-semibold tracking-tight text-foreground">
                {messages.acPartnerTitle}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {messages.acPartnerBody}
              </p>
            </article>
          </div>
          <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-border/60 bg-muted/30 p-6 sm:flex-row sm:items-center">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Server className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-base font-semibold text-foreground">{messages.acSameNoteTitle}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {messages.acSameNoteBody}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* FAQ */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.acFaqEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.acFaqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      {/* Final CTA */}
      <RevealSection className="relative overflow-hidden bg-foreground py-16 text-background sm:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-30"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.acCtaEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.acCtaTitle}
          </h2>
          <p className="mt-4 text-base text-background/80">{messages.acCtaBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.acCtaPrimary}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background sm:w-auto"
            >
              <a href={salesCta}>{messages.acCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}
